import { useSuspenseQuery } from "@tanstack/react-query";
import { createRoute, type AnyRoute } from "@tanstack/react-router";

import { loadView, viewQuery } from "@/api/views";
import { brandHead } from "@/lib/head";
import { toTab } from "@/modules/org/ui/settings-tabs";
import SettingsIntakePrintPage from "@/pages/app/settings/intake/print/page";
import PlanInterestPage from "@/pages/app/settings/plan-interest/page";
import SettingsPage from "@/pages/app/settings/page";

import { appRoute, APP_BOUNDARIES } from "./w0b";

interface SettingsSearch {
  tab?: string;
  trade?: string;
  q?: string;
  link?: string;
}

// 주소의 쿼리는 사람이 고칠 수 있다 — 글자·숫자만 문자열로 받고 나머지는 버린다(모르는 탭은 toTab 이 기본 탭으로).
const textOf = (value: unknown): string | undefined =>
  typeof value === "string" ? value : typeof value === "number" ? String(value) : undefined;

const settingsQuery = (search: SettingsSearch) =>
  viewQuery("settings", { tab: toTab(search.tab), trade: search.trade, q: search.q, link: search.link });

const SettingsRouteComponent = () => {
  const search = settingsRoute.useSearch();
  const { data } = useSuspenseQuery(settingsQuery(search));
  return <SettingsPage data={data} />;
};

export const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  validateSearch: (search: Record<string, unknown>): SettingsSearch => ({ tab: textOf(search.tab), trade: textOf(search.trade), q: textOf(search.q), link: textOf(search.link) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => loadView(context.queryClient, settingsQuery(deps)),
  head: () => brandHead("설정"),
  component: SettingsRouteComponent,
  ...APP_BOUNDARIES,
});

const intakePrintQuery = () => viewQuery("settings-intake-print");

const SettingsIntakePrintRouteComponent = () => {
  const { data } = useSuspenseQuery(intakePrintQuery());
  return <SettingsIntakePrintPage data={data} />;
};

export const settingsIntakePrintRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/intake/print",
  loader: ({ context }) => loadView(context.queryClient, intakePrintQuery()),
  head: () => brandHead("간편접수 안내문"),
  component: SettingsIntakePrintRouteComponent,
  ...APP_BOUNDARIES,
});

// 더 큰 요금제 출시 알림 신청 — 셸만 읽는다(부모 appRoute 가 읽어 둔다).
export const planInterestRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/plan-interest",
  head: () => brandHead("출시 알림 신청"),
  component: PlanInterestPage,
  ...APP_BOUNDARIES,
});

// g2c 배치의 라우트 — getParentRoute 가 가리키는 부모 아래로 root.tsx 가 꽂는다.
export const routes: AnyRoute[] = [settingsRoute, settingsIntakePrintRoute, planInterestRoute];
